import jsPDF from "jspdf";
import "jspdf-autotable";

/**
 * Formats a date string from Firestore (ISO 8601 format) into "MM-DD-YYYY".
 */
const formatDate = (isoDateString) => {
  if (!isoDateString) return "N/A";
  const date = new Date(isoDateString);
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

/**
 * Formats a 24-hour time string into 12-hour AM/PM format.
 */
const formatTime = (timeString) => {
  if (!timeString) return "N/A";
  const [hours, minutes] = timeString.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  return `${formattedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

/**
 * Generates and downloads a customized PDF with the appointment schedule.
 * Landscape letter, with wrapping for long text (e.g., Extra Equipment).
 */
export const generatePDF = (appointments) => {
  // Letter (8.5x11) in landscape gives us more horizontal space
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "letter",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const rightMargin = 14;
  const leftMargin = 14;

  // Sort chronologically by appointment date
  const sortedAppointments = [...appointments].sort(
    (a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate)
  );

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Appointment Schedule", leftMargin, 20);

  // Summary
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const dateGenerated = new Date().toLocaleString();
  doc.text(`Date Generated: ${dateGenerated}`, leftMargin, 28);
  doc.text(`Total Appointments: ${appointments.length}`, leftMargin, 35);

  // Columns (now includes Extra Equipment)
  const tableColumn = [
    "Date",
    "Start Time",
    "Client",
    "Photographers",
    "Summary",
    "Extra Equipment",
  ];
  const tableRows = [];

  sortedAppointments.forEach((appt) => {
    const photographersText = Array.isArray(appt.photographers)
      ? appt.photographers.join(", ")
      : appt.photographers || "None";

    const formattedStart = appt.start
      ? new Date(appt.start).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      : "N/A";

    tableRows.push([
      formatDate(appt.appointmentDate),
      formattedStart,
      appt.client || "N/A",
      photographersText,
      appt.summary || "N/A",
      appt.extraEquipment || "N/A", // wrap this safely
    ]);
  });

  // AutoTable with landscape-friendly widths + wrapping
  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: 40,
    margin: { left: leftMargin, right: rightMargin },
    styles: {
      fontSize: 9,                // slightly smaller for more content
      cellPadding: 3,
      halign: "left",
      overflow: "linebreak",      // ensure wrapping, not clipping
    },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 11 },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    // Keep columns wide enough to avoid vertical text, and wrap long ones
    columnStyles: {
      0: { cellWidth: 25 }, // Date
      1: { cellWidth: 25 }, // Start Time
      2: { cellWidth: 50 }, // Client
      3: { cellWidth: 50 }, // Photographers
      4: { cellWidth: 45 }, // Summary (wraps)
      5: { cellWidth: 50 }, // Extra Equipment (wraps)
    },
    // If a row gets too tall, AutoTable will continue on the next page automatically
    pageBreak: "auto",
  });

  // Footer with dynamic page count, right-aligned
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - rightMargin,
      200, // ~bottom on landscape letter
      { align: "right" }
    );
  }

  doc.save("appointment_schedule.pdf");
};
